/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useEffect, useState, useRef } from "react";
import { discovery } from "dc-core";
import classNames from "classnames";
import BottomBoardCSS from "./BottomBoard.css";

const BottomPanel = ({
  showPanel,
  panelDropinId,
  config,
  onInvisibleDivHover,
  setIsBottomPanelLoaded,
  showProximityView,
  setBottomPanelRect,
  panelProps
}) => {
  const bottomPanelRef = useRef(null);

  const [Panel, setPanel] = useState(null);

  useEffect(() => {
    if (panelDropinId) {
      discovery.loadDropinClass(panelDropinId).then(Panel => setPanel(<Panel {...panelProps} />));
    } else {
      setPanel(null);
    }
  }, [showPanel, panelDropinId]);

  useEffect(() => {
    setIsBottomPanelLoaded(!!Panel);
  }, [Panel]);

  useEffect(() => {
    setTimeout(() => {
      if (bottomPanelRef && bottomPanelRef.current) {
        const bottomPanelRect = bottomPanelRef.current.getBoundingClientRect();
        setBottomPanelRect(bottomPanelRect);
      }
    }, 0);
  });

  const viewClassNames = classNames(BottomBoardCSS.View, {
    [BottomBoardCSS.MountAnimation]: config.enableAnimation && showPanel,
    [BottomBoardCSS.UnmountAnimation]: config.enableAnimation && !showPanel
  });

  const InvisibleDiv = () => (
    <div
      className={BottomBoardCSS.InvisibleDiv}
      onMouseEnter={onInvisibleDivHover}
      tabIndex={-1}
      aria-hidden
      data-testid="bottom-board-invisible-view"
    />
  );

  const Cmp = showProximityView => {
    if (Panel) {
      return showPanel ? (
        <div data-testid="bottom-board-panel-view" className={viewClassNames} ref={bottomPanelRef}>
          {Panel}
        </div>
      ) : (
        showProximityView && <InvisibleDiv />
      );
    }
  };

  const Container = showProximityView => (
    <div data-testid="bottom-board-panel-container" className={BottomBoardCSS.PanelContainer}>
      <Cmp showProximityView={showProximityView} />
    </div>
  );

  return showPanel ? <Container /> : showProximityView && <Container showProximityView />;
};

export default BottomPanel;
